import { createSelector } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  getFilteredBalances,
  getFilteredTokens,
  getLinearData,
  getMergedTokenGroups,
  getNewExtendedBalances,
  aggregateTransactionTotals,
  aggregateSelectedTransactionSummary,
  getTransactionSummary,
  getUniqueBalanceTokens,
  getUniqueCompareTokens,
} from 'src/store/utils/balances';
import type { MergedTokenGroup } from 'src/store/utils/balances';
import {
  createSelectPriceHistory,
  selectComparisonEndDate,
  selectComparisonEndTime,
  selectComparisonStartDate,
  selectPrices,
} from 'src/store/shared/balance-history/selectors';
import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import { createCompareHistorySelectors } from 'src/store/shared/balance-history/compareHistorySelectors';

import {
  selectBalanceHash,
  selectBalancesSelection,
  selectBalanceSupportedTokenObj,
} from 'src/store/shared/balances/selectors';
import { selectJoinedAccounts } from '@skynet/data-shared-accounts';
import { selectTransactionsObj } from 'src/store/shared/transactions/selectors';

import {
  selectIgnoreTokens,
  selectIsDuplicatesVisible,
  selectIsIgnoreOtherTokens,
  selectIsInitialBalanceUsed,
  selectIsSmallBalancesVisible,
  selectShowErrorAccounts,
  selectViewChartGroupBy,
  selectViewCumulativeBalance,
  showCurrentAccounts,
  selectApplyTokenMapping,
} from 'src/store/loans/balance-table-opts/selectors';
import { selectTokenMapData } from 'src/store/shared/tokenMap/selectors';
import { selectColdWallets, selectWalletsSnapshots } from 'src/store/loans/cold-wallets/selectors';
import type { AccountView } from '@skynet/data-shared-accounts';
import type { JsonBalance, JsonBalanceExtended } from 'src/store/shared/balances/types';
import type { JsonExchange } from '@skynet/data-shared-exchanges';
import type {
  JsonBalanceHistoryExtended,
  PriceHistoryItemType,
  UniqueTokensList,
} from 'src/store/shared/balance-history/types';
import { ApplicationDomain } from 'src/routes/types';
import { selectCurrentLoan, selectIsListenSent, selectTargetLoan } from 'src/store/loans/selectors';
import { dexColdWalletApi, dexColdWalletApiEndpoints } from '@skynet/data-dex-cold-wallets';
import type { LineChartDataEntry } from 'src/store/utils/color';
import type { HiddenBalanceCounts } from 'src/store/utils/balances/core/types';
import { hiddenCountsEqualityCheck } from 'src/store/utils/balances/core/helpers';

// A stable empty-map reference so selector memoization stays intact when the
// token-mapping option is turned off.
const EMPTY_TOKEN_MAP: Record<string, string> = {};

// The token map applied to the balances views, gated by the "Apply token mapping"
// table option. When the option is off, an empty map is used so raw token symbols
// are shown instead of their configured labels.
const selectAppliedTokenMap = createSelector(
  selectTokenMapData,
  selectApplyTokenMapping,
  (tokenMap, applyTokenMapping): Record<string, string> =>
    applyTokenMapping ? tokenMap : EMPTY_TOKEN_MAP,
);

/** Compare-view price series, keyed by raw symbols and by their applied token-map labels. */
export const selectLoanPriceHistory = createSelectPriceHistory(selectAppliedTokenMap);

const selectLoansBalanceListByLoan = createSelector(
  selectCurrentLoan,
  selectBalanceHash,
  (loan, balances) => {
    if (!loan) return [];
    return balances?.[loan.id] ?? [];
  },
);

export const selectBalanceSupportTokensById = createSelector(
  selectCurrentLoan,
  selectBalanceSupportedTokenObj,
  (loan, supportExchangeObj) => {
    if (!loan) return [];
    return supportExchangeObj?.[loan.id] ?? [];
  },
);

export const selectTransactions = createSelector(
  selectCurrentLoan,
  selectTransactionsObj,
  (loan, transactions) => {
    if (!loan) return [];
    return transactions?.[loan.id] ?? [];
  },
);

export const selectLoanBalancesTokens = createSelector(
  selectLoansBalanceListByLoan,
  selectColdWallets,
  selectIsIgnoreOtherTokens,
  selectBalanceSupportTokensById,
  selectAppliedTokenMap,
  (
    balances: JsonBalance[] | undefined = [],
    coldWallets: JsonBalance[] | undefined = [],
    isIgnoreOtherTokens: boolean | undefined,
    supportedTokens: UniqueTokensList[] | undefined = [],
    tokenMap: Record<string, string> | undefined,
  ) =>
    getUniqueBalanceTokens({
      data: [...balances, ...coldWallets],
      isIgnoreOtherTokens,
      supportedTokens,
      tokenMap,
    }),
);

// The user's token selection (excluded tokens only) — independent of the
// small-balances toggle. Drives which assets each account keeps; small-balance
// hiding is applied afterwards on the resulting balances.
const selectUniqToken = createSelector(
  selectLoanBalancesTokens,
  selectIgnoreTokens,
  (tokens, ignoreTokens) => getFilteredTokens({ tokens, ignoreTokens }),
);

// Distinct raw tokens the applied token map merges into a single displayed
// label. Empty when token mapping is off (see selectAppliedTokenMap). Drives
// the "token mapping is merging tokens" warning.
export const selectLoanMergedTokenGroups = createSelector(
  selectLoansBalanceListByLoan,
  selectColdWallets,
  selectAppliedTokenMap,
  (
    balances: JsonBalance[] | undefined = [],
    coldWallets: JsonBalance[] | undefined = [],
    tokenMap: Record<string, string>,
  ): MergedTokenGroup[] =>
    getMergedTokenGroups({ balances: [...balances, ...coldWallets], tokenMap }),
);

export interface Store {
  [dexColdWalletApi.reducerPath]: ReturnType<typeof dexColdWalletApi.reducer>;
}

// balances list with exchange and account details populated

export const selectLoanBalancesExtended = createSelector(
  selectIsSmallBalancesVisible,
  selectIsDuplicatesVisible,
  selectLoansBalanceListByLoan,
  selectColdWallets,
  selectExchanges,
  selectJoinedAccounts,
  selectUniqToken,
  selectAppliedTokenMap,
  selectPrices,
  (
    isSmallBalancesVisible: boolean | undefined,
    isDuplicatesVisible: boolean | undefined,
    balances: JsonBalance[] | undefined,
    coldWallets: JsonBalance[] | undefined,
    exchanges: JsonExchange[],
    allAccounts: AccountView[],
    selectedTokens: UniqueTokensList[],
    tokenMap: Record<string, string> | undefined,
    prices: Record<string, PriceHistoryItemType[]>,
  ): JsonBalanceExtended[] =>
    getNewExtendedBalances({
      domain: ApplicationDomain.Loans,
      isSmallBalancesVisible,
      isDuplicatesVisible,
      balances,
      coldWallets,
      exchanges,
      accounts: allAccounts,
      selectedTokens,
      tokenMap,
      prices,
    }),
);

export const selectLoanHiddenBalanceCounts = createSelector(
  selectIsSmallBalancesVisible,
  selectIsDuplicatesVisible,
  selectShowErrorAccounts,
  selectLoansBalanceListByLoan,
  selectColdWallets,
  selectExchanges,
  selectJoinedAccounts,
  selectUniqToken,
  selectAppliedTokenMap,
  selectPrices,
  (
    isSmallBalancesVisible: boolean | undefined,
    isDuplicatesVisible: boolean | undefined,
    showErrorAccounts: boolean,
    balances: JsonBalance[] | undefined,
    coldWallets: JsonBalance[] | undefined,
    exchanges: JsonExchange[],
    allAccounts: AccountView[],
    selectedTokens: UniqueTokensList[],
    tokenMap: Record<string, string> | undefined,
    prices: Record<string, PriceHistoryItemType[]>,
  ): HiddenBalanceCounts => {
    const all = getNewExtendedBalances({
      domain: ApplicationDomain.Loans,
      isSmallBalancesVisible: true,
      isDuplicatesVisible: true,
      balances,
      coldWallets,
      exchanges,
      accounts: allAccounts,
      selectedTokens,
      tokenMap,
      prices,
    });
    return {
      duplicates: !isDuplicatesVisible ? all.filter((b) => b.isTotalDoubling).length : 0,
      small: !isSmallBalancesVisible
        ? all.filter((b) => b.isTotalSmallBalance || b.isComparisonSmallBalance).length
        : 0,
      errors: !showErrorAccounts ? all.filter((b) => Boolean(b.hasError)).length : 0,
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: hiddenCountsEqualityCheck,
    },
  },
);

export const selectAreBalancesReady = createSelector(
  selectIsListenSent,
  (state: Store) => state.dexColdWalletApi,
  selectTargetLoan,
  (isListenSent, walletState, targetLoan) => {
    if (!isListenSent || !targetLoan) return false;

    const loanId = Number(targetLoan);
    if (!Number.isFinite(loanId)) return false;

    const walletEndpoint = dexColdWalletApiEndpoints.listColdWallets.select({ projectId: loanId })({
      dexColdWalletApi: walletState,
    });

    const readyStatuses: QueryStatus[] = [QueryStatus.fulfilled, QueryStatus.rejected];
    return readyStatuses.includes(walletEndpoint.status);
  },
);

// Full (unfiltered) extended history for the compare table and the compare list. The table one
// also builds the compare token universe, so it stays stable regardless of the small-balances
// toggle; display lists apply small / duplicate hiding below.
const {
  selectHistory: selectLoanBalancesHistoryExtendedRaw,
  selectHistoryUseTransactionForDiff: selectLoanBalancesHistoryExtendedUseTransactionForDiffRaw,
} = createCompareHistorySelectors({
  isInitialBalanceUsed: selectIsInitialBalanceUsed,
  balances: selectLoansBalanceListByLoan,
  coldWallets: selectColdWallets,
  coldWalletsSnapshots: selectWalletsSnapshots,
  transactions: selectTransactions,
  supportedTokens: selectBalanceSupportTokensById,
  tokenMap: selectAppliedTokenMap,
  showCurrentAccounts,
  prices: selectLoanPriceHistory,
  ignoreTokens: selectIgnoreTokens,
  isIgnoreOtherTokens: selectIsIgnoreOtherTokens,
});

// Display list: the full history with small-balance / duplicate accounts hidden
// per the current toggles.
export const selectLoanBalancesHistoryExtended = createSelector(
  selectLoanBalancesHistoryExtendedRaw,
  selectIsSmallBalancesVisible,
  selectIsDuplicatesVisible,
  (data, isSmallBalancesVisible, isDuplicatesVisible) =>
    getFilteredBalances({ data, isSmallBalancesVisible, isDuplicatesVisible }),
);

export const selectLinearData = createSelector(
  selectLoanBalancesHistoryExtended,
  selectBalancesSelection,
  selectComparisonStartDate,
  selectComparisonEndDate,
  selectComparisonEndTime,
  selectViewChartGroupBy,
  selectIgnoreTokens,
  selectViewCumulativeBalance,
  (
    data,
    selection,
    startDate,
    endDate,
    endTime,
    viewChartGroupBy,
    ignoreTokens,
    viewCumulativeBalance,
  ): LineChartDataEntry<JsonBalanceHistoryExtended>[] =>
    getLinearData({
      data,
      selection,
      viewChartGroupBy,
      ignoreTokens,
      viewCumulativeBalance,
      startDate,
      endDate,
      endTime,
    }),
);

export const selectLoanCompareTokens = createSelector(
  selectLoanBalancesHistoryExtendedRaw,
  selectIsIgnoreOtherTokens,
  selectAppliedTokenMap,
  (
    history: JsonBalanceHistoryExtended[] | undefined,
    isIgnoreOther: boolean,
    tokenMap: Record<string, string> | undefined,
  ): UniqueTokensList[] => {
    return getUniqueCompareTokens({
      data: history,
      isIgnoreOther,
      tokenMap,
    });
  },
);

// Compare list: the same history with transactions applied to the net change,
// and small-balance / duplicate accounts hidden per the current toggles.
export const selectLoanBalancesHistoryExtendedUseTransactionForDiff = createSelector(
  selectLoanBalancesHistoryExtendedUseTransactionForDiffRaw,
  selectIsSmallBalancesVisible,
  selectIsDuplicatesVisible,
  (data, isSmallBalancesVisible, isDuplicatesVisible) =>
    getFilteredBalances({ data, isSmallBalancesVisible, isDuplicatesVisible }),
);

const selectLoanRawTransactionSummary = createSelector(
  selectTransactions,
  selectComparisonStartDate,
  selectComparisonEndDate,
  getTransactionSummary,
);

export const selectLoanTransactionTotals = createSelector(
  selectLoanRawTransactionSummary,
  aggregateTransactionTotals,
);

export const selectLoanSelectedTransactionSummary = createSelector(
  selectLoanRawTransactionSummary,
  selectBalancesSelection,
  aggregateSelectedTransactionSummary,
);

export const selectLoanTransactionSummary = createSelector(
  selectLoanRawTransactionSummary,
  selectLoanTransactionTotals,
  selectLoanSelectedTransactionSummary,
  (raw, totals, selected) => ({ ...raw, ...totals, ...selected }),
);
